"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePagination = usePagination;
const react_1 = require("react");
const client_1 = require("@apollo/client");
const createCustomOffsetCursor = (offset = 0) => btoa(`OffsetConnection:${offset}`);
const calculateLastPageSize = (pageSize, total = 0) => {
    const fullPages = Math.floor(total / pageSize);
    const remainder = total - fullPages * pageSize;
    return remainder === 0 ? pageSize : remainder;
};
const getForwardNative = (end, size) => ({
    after: end, first: size, before: null, last: null,
});
const getBackNative = (start, size) => ({
    before: start, last: size, after: null, first: null,
});
const getFirst = (size) => ({
    first: size, after: null, before: null, last: null,
});
const getLast = (size, total) => getBackNative(createCustomOffsetCursor(total), size);
const getOffset = (offset, size) => ({
    after: createCustomOffsetCursor(offset),
    first: size,
    before: null,
    last: null,
});
const getPaginationVariables = ({ nextPage, currentPage, pageSize, totalCount, pageInfo, cursorStrategy, }) => {
    const step = nextPage - currentPage;
    const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : Infinity;
    const isLastPage = nextPage === totalPages;
    const wantNative = cursorStrategy === 'native' || Math.abs(step) === 1;
    // Handle first page - always use simple first page query
    if (nextPage === 1) {
        return getFirst(pageSize);
    }
    // Handle last page - use backward pagination with correct page size
    if (isLastPage) {
        return getLast(calculateLastPageSize(pageSize, totalCount), totalCount);
    }
    // Handle sequential forward navigation with native cursors
    if (wantNative && step === 1 && (pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.endCursor)) {
        return getForwardNative(pageInfo.endCursor, pageSize);
    }
    // Handle sequential backward navigation with native cursors
    if (wantNative && step === -1 && (pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.startCursor)) {
        return getBackNative(pageInfo.startCursor, pageSize);
    }
    // Handle any other navigation (jumps) using offset-based pagination
    return getOffset((nextPage - 1) * pageSize, pageSize);
};
function usePagination({ pageSize, pageInfo, totalCount = 0, fetchMoreOptions, networkStatus, lastVariables, fetchMore, refetch, cursorStrategy = 'native', }) {
    const [currentPage, setCurrentPage] = (0, react_1.useState)(1);
    // reset when the query is rerun without before/after
    (0, react_1.useEffect)(() => {
        if (!lastVariables)
            return;
        const { before, after } = lastVariables;
        if (!before && !after) {
            setCurrentPage(1);
        }
    }, [lastVariables]);
    const onPageChange = (0, react_1.useCallback)((nextPage) => __awaiter(this, void 0, void 0, function* () {
        const paginationVars = getPaginationVariables({
            nextPage,
            currentPage,
            pageSize,
            totalCount,
            pageInfo,
            cursorStrategy,
        });
        const _a = fetchMoreOptions !== null && fetchMoreOptions !== void 0 ? fetchMoreOptions : {}, { variables: fetchMoreOptionsVariables } = _a, rest = __rest(_a, ["variables"]);
        const newQueryVars = Object.assign(Object.assign({}, (fetchMoreOptionsVariables !== null && fetchMoreOptionsVariables !== void 0 ? fetchMoreOptionsVariables : {})), paginationVars);
        if (refetch) {
            // refetchQueries will not have the updated variables if fetchMore is used (the page is always 1)
            yield refetch(newQueryVars);
        }
        else if (fetchMore) {
            yield fetchMore(Object.assign({ variables: newQueryVars, updateQuery: (_, { fetchMoreResult }) => fetchMoreResult }, rest));
        }
        setCurrentPage(nextPage);
    }), [
        currentPage,
        pageSize,
        totalCount,
        pageInfo,
        cursorStrategy,
        fetchMoreOptions,
        fetchMore,
        refetch,
    ]);
    const onNextPage = (0, react_1.useCallback)(() => {
        if ((pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage) === false)
            return;
        return onPageChange(currentPage + 1);
    }, [pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage, onPageChange, currentPage]);
    const onPreviousPage = (0, react_1.useCallback)(() => {
        if (currentPage === 1)
            return;
        return onPageChange(currentPage - 1);
    }, [currentPage, onPageChange]);
    // expose API – memoised to avoid renders
    return (0, react_1.useMemo)(() => ({
        onPageChange,
        onNextPage,
        onPreviousPage,
        setCurrentPage,
        hasNextPage: pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage,
        pageNumber: currentPage,
        isLoadingMore: networkStatus === client_1.NetworkStatus.fetchMore,
    }), [
        onPageChange,
        onNextPage,
        onPreviousPage,
        pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage,
        currentPage,
        networkStatus,
    ]);
}
